import React, { useState, useEffect, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { ArrowRight, Music } from 'lucide-react';
import { motion, useScroll, useTransform, useSpring, useInView } from 'framer-motion';
import { ArtistCardSkeleton } from '../components/LoadingSkeleton';
import { api } from '../api';

/* ── Utility components ── */

function Parallax({ children, speed = 0.3 }) {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ['start end', 'end start'] });
  const y = useTransform(scrollYProgress, [0, 1], [speed * -120, speed * 120]);
  const smoothY = useSpring(y, { stiffness: 80, damping: 20 });
  return (
    <div ref={ref} style={{ overflow: 'visible' }}>
      <motion.div style={{ y: smoothY }}>{children}</motion.div>
    </div>
  );
}

function Reveal({ children, direction = 'up', delay = 0, className = '' }) {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: '-60px' });
  const dirs = { up: { y: 60, x: 0 }, down: { y: -60, x: 0 }, left: { y: 0, x: -80 }, right: { y: 0, x: 80 } };
  const d = dirs[direction] || dirs.up;
  return (
    <motion.div ref={ref} className={className}
      initial={{ opacity: 0, y: d.y, x: d.x }}
      animate={isInView ? { opacity: 1, y: 0, x: 0 } : {}}
      transition={{ duration: 0.8, delay, ease: [0.22, 1, 0.36, 1] }}
    >{children}</motion.div>
  );
}

function ScrollLine() {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ['start end', 'end center'] });
  const width = useTransform(scrollYProgress, [0, 1], ['0%', '100%']);
  return (
    <div ref={ref} className="scroll-line-track">
      <motion.div className="scroll-line-fill" style={{ width }} />
    </div>
  );
}

/* Infinite horizontal marquee */
function Marquee({ text, speed = 30 }) {
  return (
    <div className="marquee-wrap">
      <motion.div
        className="marquee-track"
        animate={{ x: ['0%', '-50%'] }}
        transition={{ duration: speed, repeat: Infinity, ease: 'linear' }}
      >
        {Array.from({ length: 8 }).map((_, i) => (
          <span key={i} className="marquee-item">
            {text} <span className="marquee-dot" />
          </span>
        ))}
      </motion.div>
    </div>
  );
}

/* Rotating ring that spins on scroll */
function ScrollRing({ size = 200 }) {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ['start end', 'end start'] });
  const rotate = useTransform(scrollYProgress, [0, 1], [0, 360]);
  const smoothRotate = useSpring(rotate, { stiffness: 40, damping: 20 });
  return (
    <div ref={ref} className="scroll-ring-container">
      <motion.svg
        width={size} height={size} viewBox="0 0 200 200"
        className="scroll-ring-svg"
        style={{ rotate: smoothRotate }}
      >
        <circle cx="100" cy="100" r="90" stroke="currentColor" strokeWidth="0.5" fill="none" opacity="0.15"
          strokeDasharray="8 12" />
        <circle cx="100" cy="100" r="70" stroke="currentColor" strokeWidth="0.3" fill="none" opacity="0.1"
          strokeDasharray="4 16" />
      </motion.svg>
    </div>
  );
}

/* Big quote that scales & fades on scroll */
function ScrollQuote({ text }) {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ['start end', 'end start'] });
  const scale = useTransform(scrollYProgress, [0, 0.3, 0.5, 0.7, 1], [0.7, 1, 1.05, 1, 0.7]);
  const opacity = useTransform(scrollYProgress, [0, 0.25, 0.5, 0.75, 1], [0, 1, 1, 1, 0]);
  const smoothScale = useSpring(scale, { stiffness: 60, damping: 20 });
  return (
    <div ref={ref} className="scroll-quote-section">
      <motion.p className="scroll-quote-text" style={{ scale: smoothScale, opacity }}>
        {text}
      </motion.p>
    </div>
  );
}

/* Letter-by-letter text reveal */
function SplitTextReveal({ text, className = '' }) {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: '-60px' });
  return (
    <span ref={ref} className={className}>
      {text.split('').map((char, i) => (
        <motion.span
          key={i}
          style={{ display: 'inline-block' }}
          initial={{ opacity: 0, y: 30, rotateX: -90 }}
          animate={isInView ? { opacity: 1, y: 0, rotateX: 0 } : {}}
          transition={{ duration: 0.5, delay: i * 0.03, ease: [0.22, 1, 0.36, 1] }}
        >
          {char === ' ' ? '\u00A0' : char}
        </motion.span>
      ))}
    </span>
  );
}

/* Floating particles */
function FloatingParticles() {
  const particles = useRef(
    Array.from({ length: 20 }).map((_, i) => ({
      id: i,
      x: Math.random() * 100,
      y: Math.random() * 100,
      size: 2 + Math.random() * 3,
      dur: 15 + Math.random() * 20,
      delay: Math.random() * -20,
    }))
  ).current;

  return (
    <div className="floating-particles">
      {particles.map((p) => (
        <motion.div
          key={p.id}
          className="particle"
          style={{
            left: `${p.x}%`,
            top: `${p.y}%`,
            width: p.size,
            height: p.size,
          }}
          animate={{
            y: [-20, 20, -20],
            x: [-10, 10, -10],
            opacity: [0.15, 0.4, 0.15],
          }}
          transition={{
            duration: p.dur,
            repeat: Infinity,
            delay: p.delay,
            ease: 'easeInOut',
          }}
        />
      ))}
    </div>
  );
}

/* ── Main component ── */

export default function Home() {
  const [artists, setArtists] = useState([]);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  const heroRef = useRef(null);
  const { scrollYProgress: heroProgress } = useScroll({ target: heroRef, offset: ['start start', 'end start'] });
  const heroContentY = useSpring(useTransform(heroProgress, [0, 1], [0, -150]), { stiffness: 60, damping: 20 });
  const heroOpacity = useTransform(heroProgress, [0, 0.6], [1, 0]);
  const heroScale = useTransform(heroProgress, [0, 0.6], [1, 0.92]);
  const videoScale = useTransform(heroProgress, [0, 1], [1, 1.15]);

  useEffect(() => {
    api.getArtists()
      .then((data) => { setArtists(data.artists || []); setLoading(false); })
      .catch(() => setLoading(false));
  }, []);

  const featuredArtists = artists.slice(0, 6);

  return (
    <div className="home-scroll-fx" data-testid="home-page">
      <FloatingParticles />

      {/* ===== HERO ===== */}
      <section className="hero" ref={heroRef}>
        <motion.div className="hero-video-wrapper" style={{ scale: videoScale }}>
          <video className="hero-video" autoPlay loop muted playsInline preload="auto" data-testid="hero-video">
            <source src="https://lofimusic.sgp1.digitaloceanspaces.com/videos/hero-home.webm" type="video/webm" />
          </video>
        </motion.div>
        <div className="hero-video-overlay" />
        <motion.div className="hero-content" style={{ y: heroContentY, opacity: heroOpacity, scale: heroScale }}>
          <motion.div className="hero-label"
            initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.2 }}
          >
            <Music size={14} /> Independent Music Label
          </motion.div>
          <motion.h1 initial={{ opacity: 0, y: 40 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 1, delay: 0.5 }}>
            <span className="hero-line-1">Where Sound Meets</span>
            <motion.span className="accent hero-accent"
              initial={{ opacity: 0, scale: 0.8 }} animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 1.2, delay: 1.0, ease: "easeOut" }}
            >Serenity</motion.span>
          </motion.h1>
          <motion.p className="hero-subtitle"
            initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 1.4 }}
          >
            CloudfiMusic is an independent lofi music label nurturing artists
            who craft ambient soundscapes, warm melodies, and chill beats
            that move through you like clouds across the sky.
          </motion.p>
          <motion.div className="hero-actions"
            initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 1.7 }}
          >
            <Link to="/artists" className="btn btn-primary" data-testid="hero-cta-artists">
              Explore Artists <ArrowRight size={16} />
            </Link>
            <Link to="/releases" className="btn btn-secondary" data-testid="hero-cta-releases">
              Browse Releases
            </Link>
          </motion.div>
        </motion.div>

        <motion.div className="hero-scroll-indicator" style={{ opacity: heroOpacity }}>
          <motion.div className="hero-scroll-dot"
            animate={{ y: [0, 12, 0] }}
            transition={{ duration: 1.8, repeat: Infinity, ease: 'easeInOut' }}
          />
        </motion.div>
      </section>

      {/* ===== Marquee banner ===== */}
      <Marquee text="CloudfiMusic" speed={25} />

      {/* ===== Big scroll quote ===== */}
      <ScrollQuote text={`"Music should be a sanctuary — a place where the world slows down."`} />

      <ScrollLine />

      {/* ===== Featured Artists ===== */}
      <section className="section featured-section" style={{ position: 'relative' }}>
        <ScrollRing size={300} />
        <div className="section-inner">
          <Reveal direction="up">
            <div className="section-header">
              <h2><SplitTextReveal text="Our Artists" /></h2>
              <p>Discover the talented producers on the CloudfiMusic roster</p>
            </div>
          </Reveal>

          <div className="home-artists-grid">
            {loading
              ? Array.from({ length: 6 }).map((_, i) => <ArtistCardSkeleton key={i} />)
              : featuredArtists.map((artist, index) => (
                  <Parallax key={artist.spotify_id} speed={index % 2 === 0 ? 0.15 : -0.1}>
                    <Reveal delay={index * 0.07}>
                      <div className="home-artist-item"
                        onClick={() => navigate(`/artists/${artist.spotify_id}`)}
                        role="button" tabIndex={0}
                        onKeyDown={(e) => e.key === 'Enter' && navigate(`/artists/${artist.spotify_id}`)}
                        data-testid={`home-artist-${artist.spotify_id}`}
                      >
                        <div className="home-artist-img">
                          {artist.image_url ? (
                            <img src={artist.image_url} alt={artist.name} loading="lazy" />
                          ) : (
                            <div className="home-artist-placeholder"><Music size={32} color="var(--text-muted)" /></div>
                          )}
                          <div className="home-artist-img-overlay" />
                        </div>
                        <span className="home-artist-name">{artist.name}</span>
                      </div>
                    </Reveal>
                  </Parallax>
                ))}
          </div>

          {!loading && artists.length > 6 && (
            <Reveal delay={0.3}>
              <div style={{ textAlign: 'center', marginTop: '2.5rem' }}>
                <Link to="/artists" className="btn btn-secondary" data-testid="view-all-artists">
                  View All Artists <ArrowRight size={16} />
                </Link>
              </div>
            </Reveal>
          )}
        </div>
      </section>

      <ScrollLine />

      {/* ===== Ethos ===== */}
      <section className="ethos-section">
        <div className="ethos-ambient">
          <div className="ethos-orb ethos-orb-1" />
          <div className="ethos-orb ethos-orb-2" />
        </div>
        <div className="ethos-inner">
          <Reveal direction="up">
            <div className="ethos-header" style={{ textAlign: 'center', marginBottom: '5rem' }}>
              <span className="ethos-label">Our Philosophy</span>
              <h2 className="ethos-title">
                <SplitTextReveal text="Built on " />
                <span><SplitTextReveal text="Intention" /></span>
              </h2>
            </div>
          </Reveal>

          <div className="ethos-grid">
            {[
              { icon: (<svg viewBox="0 0 60 60" className="ethos-ring-svg"><circle className="ring ring-outer" cx="30" cy="30" r="28" /><circle className="ring ring-mid" cx="30" cy="30" r="20" /><circle className="ring ring-inner" cx="30" cy="30" r="12" /><circle className="ring-dot" cx="30" cy="30" r="3" /></svg>), title: 'Curated Releases', accent: 'Sound is sacred.', desc: 'Every release on CloudfiMusic is carefully selected and produced to maintain the signature sound our listeners have come to love.', dir: 'left' },
              { icon: (<svg viewBox="0 0 60 60" className="ethos-ring-svg"><path className="ring ring-wave-1" d="M30 6 L30 54" /><path className="ring ring-wave-2" d="M20 14 L20 46" /><path className="ring ring-wave-3" d="M40 14 L40 46" /><path className="ring ring-wave-4" d="M10 22 L10 38" /><path className="ring ring-wave-5" d="M50 22 L50 38" /></svg>), title: 'Artist Development', accent: 'Freedom to create.', desc: 'As an independent label, we invest in our artists\u2019 growth. They retain creative freedom while we handle distribution, promotion, and brand building.', dir: 'up' },
              { icon: (<svg viewBox="0 0 60 60" className="ethos-ring-svg"><polygon className="ring ring-hex-outer" points="30,2 55,16 55,44 30,58 5,44 5,16" /><polygon className="ring ring-hex-inner" points="30,12 47,21 47,39 30,48 13,39 13,21" /><circle className="ring-dot" cx="30" cy="30" r="3" /></svg>), title: 'Independent Spirit', accent: 'Art over algorithm.', desc: 'CloudfiMusic operates independently, free from major-label constraints. We make decisions driven by artistry, not algorithms.', dir: 'right' },
            ].map((item, index) => (
              <Parallax key={index} speed={index === 1 ? -0.12 : 0.12}>
                <Reveal direction={item.dir} delay={index * 0.12}>
                  <div className="ethos-card">
                    <div className="ethos-card-ring">{item.icon}<div className="ethos-card-glow" /></div>
                    <span className="ethos-card-accent">{item.accent}</span>
                    <h3 className="ethos-card-title">{item.title}</h3>
                    <p className="ethos-card-desc">{item.desc}</p>
                    <div className="ethos-card-border" />
                  </div>
                </Reveal>
              </Parallax>
            ))}
          </div>
        </div>
      </section>

      {/* ===== Final marquee ===== */}
      <Marquee text={"Lofi \u00b7 Ambient \u00b7 Chill \u00b7 CloudfiMusic"} speed={35} />
    </div>
  );
}
